import {
  ChangeEvent,
  ClipboardEvent,
  CSSProperties,
  DragEvent,
  KeyboardEvent,
  UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { listProjectAssets } from '../../services/projectAssets';
import { chatImageUploadResultToMessageImage, deleteChatImage, uploadChatImage } from '../../services/chats';
import { ChatMessageImage } from '../../models/chat';
import { ASSET_REFERENCE_BG, ASSET_REFERENCE_COLOR, splitAssetMentions } from './assetMentions';
import { IconImage, IconSend, IconX } from '../Icons';
import InlineError from '../InlineError';
import Spinner from '../Spinner';
import { actionFailureMessage } from '../../utils/actionFailure';

const MAX_IMAGES_PER_MESSAGE = 3;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg'];
const MESSAGE_BOX_MAX_HEIGHT = 'min(40vh, 240px)';

/** Shared by the textarea and the overlay that draws its text, so the caret sits exactly on the drawn text. */
const MESSAGE_TEXT_STYLE: CSSProperties = {
  boxSizing: 'border-box',
  margin: 0,
  border: 'none',
  padding: '4px 6px',
  font: '400 13px/1.55 var(--font-sans)',
  letterSpacing: 'normal',
  wordSpacing: 'normal',
  textIndent: 0,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin',
};

interface ChatComposerProps {
  /** Resolves true when the message was accepted; the composer keeps its text and images until then. */
  onSend: (content: string, attachments: ChatMessageImage[]) => Promise<boolean>;
  isAnyRunning: boolean;
  isCurrentRunning: boolean;
  /** The project's values are being arranged into panels; a request sent now waits for it. */
  organizing: boolean;
  sendError: string | null;
  suggestions?: string[];
  projectId?: string;
  chatId?: string;
}

interface MentionToken {
  start: number;
  prefix: string;
}

function findMentionToken(text: string, caret: number): MentionToken | null {
  const beforeCaret = text.slice(0, caret);
  const match = beforeCaret.match(/(?:^|[\s.,!?()])\/([A-Za-z0-9_-]*)$/);
  if (!match) return null;
  const prefix = match[1];
  return { start: caret - prefix.length - 1, prefix };
}

export default function ChatComposer({
  onSend,
  isAnyRunning,
  isCurrentRunning,
  organizing,
  sendError,
  suggestions,
  projectId,
  chatId,
}: ChatComposerProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [assetNames, setAssetNames] = useState<string[] | null>(null);
  const [mention, setMention] = useState<MentionToken | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [attachments, setAttachments] = useState<ChatMessageImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [imageError, setImageError] = useState<string | null>(null);

  const disabled = isAnyRunning || submitting;
  const showOtherChatBanner = isAnyRunning && !isCurrentRunning;
  const canAttachMore = attachments.length < MAX_IMAGES_PER_MESSAGE;

  const mentionMatches =
    mention && assetNames
      ? assetNames.filter((n) => n.toLowerCase().startsWith(mention.prefix.toLowerCase())).slice(0, 6)
      : [];
  const showMentionDropdown = mention != null && mentionMatches.length > 0;

  const refreshMentionState = useCallback(
    (text: string, caret: number) => {
      if (!projectId) return;
      const token = findMentionToken(text, caret);
      setMention(token);
      setHighlightIndex(0);
      const dropdownJustOpened = token != null && mention == null;
      if (token && (assetNames === null || dropdownJustOpened)) {
        listProjectAssets(projectId)
          .then((assets) => setAssetNames(assets.map((a) => a.name)))
          .catch(() => setAssetNames((prev) => prev ?? []));
      }
    },
    [projectId, assetNames, mention],
  );

  const insertMention = (name: string) => {
    if (!mention) return;
    const caret = textareaRef.current?.selectionStart ?? value.length;
    const newValue = `${value.slice(0, mention.start)}/${name} ${value.slice(caret)}`;
    setValue(newValue);
    setMention(null);
    const newCaret = mention.start + name.length + 2;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCaret, newCaret);
    });
  };

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!projectId || files.length === 0) return;
      setImageError(null);

      const remainingSlots = MAX_IMAGES_PER_MESSAGE - attachments.length;
      if (remainingSlots <= 0) {
        setImageError(`You can attach at most ${MAX_IMAGES_PER_MESSAGE} images per message.`);
        return;
      }

      const accepted: File[] = [];
      for (const file of files.slice(0, remainingSlots)) {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          setImageError('Only PNG and JPG images are supported.');
          continue;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          setImageError('Each image must be 5MB or smaller.');
          continue;
        }
        accepted.push(file);
      }
      if (files.length > remainingSlots) {
        setImageError(`You can attach at most ${MAX_IMAGES_PER_MESSAGE} images per message.`);
      }
      if (accepted.length === 0) return;

      setIsUploading(true);
      try {
        for (const file of accepted) {
          try {
            const result = await uploadChatImage(projectId, file, chatId);
            setAttachments((prev) => [...prev, chatImageUploadResultToMessageImage(result)]);
          } catch (err) {
            const message =
              (typeof err === 'object' && err !== null && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                : null) ?? 'Failed to upload image. Please try again.';
            setImageError(actionFailureMessage(err, message));
          }
        }
      } finally {
        setIsUploading(false);
      }
    },
    [projectId, chatId, attachments.length],
  );

  const onFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    void uploadFiles(files);
    e.target.value = '';
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      void uploadFiles(files);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      void uploadFiles(files);
    }
  };

  const removeAttachment = async (id: string) => {
    setImageError(null);
    if (!projectId) {
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      return;
    }
    setRemovingIds((prev) => new Set(prev).add(id));
    try {
      await deleteChatImage(projectId, id);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setImageError(actionFailureMessage(err, 'Failed to remove image. Please try again.'));
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const submit = async (overrideValue?: string) => {
    const candidate = (overrideValue ?? value).trim();
    if (!candidate || isAnyRunning || submittingRef.current) return;
    const toSend = attachments;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (!(await onSend(candidate, toSend))) return;
      setValue('');
      setMention(null);
      setAttachments([]);
      setImageError(null);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    refreshMentionState(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const onScroll = (e: UIEvent<HTMLTextAreaElement>) => {
    if (!overlayRef.current) return;
    overlayRef.current.scrollTop = e.currentTarget.scrollTop;
    overlayRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };

  const fitToText = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? 'auto' : 'hidden';
    if (overlayRef.current) overlayRef.current.scrollTop = textarea.scrollTop;
  }, []);

  useLayoutEffect(fitToText, [value, fitToText]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    let width = textarea.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (textarea.offsetWidth === width) return;
      width = textarea.offsetWidth;
      fitToText();
    });
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [fitToText]);

  const segments = splitAssetMentions(value, assetNames ?? []);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionMatches[highlightIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMention(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div style={{ padding: '12px 24px 20px' }}>
      {showOtherChatBanner && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--warn)',
            border: '1px solid rgba(247,194,107,0.25)',
            background: 'rgba(247,194,107,0.06)',
            padding: '8px 12px',
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          Another chat in this project is running — wait for it to finish.
        </div>
      )}

      {organizing && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            fontSize: 12,
            color: 'var(--text-2)',
            border: '1px solid var(--line-2)',
            background: 'var(--surface)',
            padding: '8px 12px',
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          <span style={{ display: 'grid', placeItems: 'center', height: 18 }}>
            <Spinner size={14} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div>{t('chat.organizing')}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t('chat.organizingNote')}</div>
          </div>
        </div>
      )}

      <div className="composer" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
        {(attachments.length > 0 || isUploading) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {attachments.map((img) => {
              const isRemoving = removingIds.has(img.id);
              return (
                <div
                  key={img.id}
                  style={{
                    position: 'relative',
                    width: 56,
                    height: 56,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid var(--line-2)',
                    flex: 'none',
                  }}
                >
                  <img
                    src={img.thumbnailUrl}
                    alt="attachment"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isRemoving ? 0.4 : 1 }}
                  />
                  {isRemoving ? (
                    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.25)' }}>
                      <Spinner />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeAttachment(img.id)}
                      aria-label="Remove image"
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 18,
                        height: 18,
                        borderRadius: 9,
                        border: 'none',
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <IconX size={11} />
                    </button>
                  )}
                </div>
              );
            })}
            {isUploading && (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 8,
                  border: '1px dashed var(--line-2)',
                  display: 'grid',
                  placeItems: 'center',
                  flex: 'none',
                }}
              >
                <Spinner />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, position: 'relative' }}>
          {showMentionDropdown && (
            <div
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 6px)',
                left: 0,
                minWidth: 220,
                background: 'var(--surface)',
                border: '1px solid var(--line-2)',
                borderRadius: 10,
                padding: 4,
                zIndex: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
              }}
            >
              {mentionMatches.map((name, index) => (
                <button
                  key={name}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMention(name);
                  }}
                  onMouseEnter={() => setHighlightIndex(index)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    border: 'none',
                    borderRadius: 7,
                    cursor: 'pointer',
                    background: index === highlightIndex ? 'var(--bg-2)' : 'transparent',
                    color: 'var(--text)',
                    font: '500 13px var(--font-mono)',
                  }}
                >
                  /{name}
                </button>
              ))}
              <div className="muted" style={{ fontSize: 11, padding: '4px 10px' }}>
                Your uploaded assets
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || !projectId || !canAttachMore}
            className="btn ghost sm"
            title={canAttachMore ? 'Attach an image' : `Up to ${MAX_IMAGES_PER_MESSAGE} images per message`}
            aria-label="Attach an image"
            style={{ height: 32, flex: 'none' }}
          >
            <IconImage />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            multiple
            onChange={onFileInputChange}
            style={{ display: 'none' }}
          />

          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <div
              ref={overlayRef}
              aria-hidden="true"
              style={{
                ...MESSAGE_TEXT_STYLE,
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                color: 'var(--text)',
                opacity: disabled ? 0.7 : 1,
              }}
            >
              {segments.map((seg, i) =>
                seg.isReference ? (
                  <span
                    key={i}
                    style={{
                      color: ASSET_REFERENCE_COLOR,
                      background: ASSET_REFERENCE_BG,
                      borderRadius: 4,
                    }}
                  >
                    {seg.text}
                  </span>
                ) : (
                  <span key={i}>{seg.text}</span>
                ),
              )}
              {value.endsWith('\n') && '\u200b'}
            </div>
            <textarea
              ref={textareaRef}
              className="textarea"
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onScroll={onScroll}
              onPaste={onPaste}
              onClick={(e) =>
                refreshMentionState(value, (e.target as HTMLTextAreaElement).selectionStart ?? value.length)
              }
              onBlur={() => setMention(null)}
              rows={2}
              disabled={disabled}
              placeholder={
                disabled
                  ? 'Send disabled while a run is active…'
                  : 'Reply, request a change, or ask why something works the way it does…'
              }
              style={{
                ...MESSAGE_TEXT_STYLE,
                display: 'block',
                background: 'transparent',
                minHeight: 0,
                maxHeight: MESSAGE_BOX_MAX_HEIGHT,
                width: '100%',
                position: 'relative',
                color: 'transparent',
                caretColor: 'var(--text)',
                opacity: disabled ? 0.7 : 1,
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
            <button
              type="button"
              onClick={() => submit()}
              disabled={disabled || !value.trim()}
              className="btn primary sm"
              style={{ height: 32 }}
            >
              Send
              <IconSend />
            </button>
          </div>
        </div>
      </div>

      {attachments.length > 0 && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          Attached images are used only to analyze screenshots or describe a style. To use an image in your game,
          add it in the Assets panel and reference it with “/”.
        </div>
      )}

      {suggestions && suggestions.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => submit(s)}
              disabled={disabled}
              className="chip"
              style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {imageError && (
        <div style={{ marginTop: 8 }}>
          <InlineError>{imageError}</InlineError>
        </div>
      )}

      {sendError && (
        <div style={{ marginTop: 8 }}>
          <InlineError>{sendError}</InlineError>
        </div>
      )}
    </div>
  );
}
